/**
 * SEO + JSON-LD pour la page /a/[slug] (adhésion).
 * Sort le bloc useHead + useSeoMeta du composant index.vue pour réduire la taille.
 *
 * SEO sprint v2 (Karim 2026-06-15) :
 * - noindex sur 404 / draft / archived (évite polluer l'index Google avec des
 *   pages inexistantes/non-publiques)
 * - BreadcrumbList : ajout de `item` sur la dernière entrée (sans ça Google
 *   ignore parfois le rich snippet) + canonical en URL absolue
 * - title enrichi avec asso + ville du lieu (`villeCourteLieu`, territoire ou
 *   pays hors métropole) pour les requêtes géo
 * - JSON-LD Product : PAS d'aggregateRating (un compteur de membres n'est pas
 *   un avis — une note fabriquée = fake review, cf. audit SemRush 2026-08-05)
 */
import type { MembershipPlanPublic, MembershipPlanStatus } from "~/types/membership";
import { getDirectusFileId } from "@donoor/types/payment";
import { stripHtml, truncateForMeta } from "@donoor/utils/string";
import { villeCourteLieu } from "#shared/utils/lieuPublic";
import { LOCALE_INTL, type Langue } from "~/utils/langue";
import { useDemoAccueil } from "~/composables/useDemoAccueil";

export function useMembershipCheckoutSeo(
  plan: Ref<MembershipPlanPublic | null>,
  slug: Ref<string>,
) {
  const { t, locale } = useI18n();
  const { formatCurrency } = useFormatLangue();
  const config = useRuntimeConfig();
  // Origine du site COURANT (prod/staging/préview) pour les og:image
  // auto-référentes — cf. useShareBaseUrl. Canonical reste prod en dur.
  const shareBase = useShareBaseUrl();
  const fallbackOgImage = `${shareBase}/og-image.png`;

  // Titre affiché dans les meta : celui du GROUPE (sans le suffixe « — {tarif} »
  // du plan-leader) — la page publique propose TOUS les tarifs, le title/OG ne
  // doit pas coller le nom d'UN tarif. Fallback title brut (API pas encore
  // redéployée / donnée legacy). Karim 2026-08-04.
  const displayTitle = computed(
    () => plan.value?.group_title || plan.value?.title || "",
  );

  const ogDescription = computed(() => {
    const desc = truncateForMeta(stripHtml(plan.value?.description), 150);
    const orgName = plan.value?.organization_name || "";
    const price = Number(plan.value?.price || 0);
    const prix = price > 0
      ? t("adhesion.useMembershipCheckoutSeo.prixA", { montant: formatCurrency(price) })
      : t("adhesion.useMembershipCheckoutSeo.prixGratuite");
    return desc || t("adhesion.useMembershipCheckoutSeo.description", {
      organisation: orgName,
      titre: displayTitle.value,
      prix,
    });
  });

  const planCoverImageId = computed(() => getDirectusFileId(plan.value?.cover_image));

  // OG image dynamique (sprint v4 Karim 2026-06-15) : route /api/og/membership
  // qui génère un PNG cyan 1200×630 avec titre + prix + durée + asso + ville.
  // CTR partage social ~30% supérieur à la simple cover_image hero.
  // Fallback cover_image puis logo asso puis og-image.png si slug inconnu.
  const ogImageUrl = computed(() => {
    if (slug.value) return `${shareBase}/api/og/membership?slug=${slug.value}`;
    const coverId = planCoverImageId.value;
    if (coverId) {
      return `${config.public.apiUrl}/api/assets/${coverId}?width=1200&height=630&fit=cover&format=jpg&quality=80`;
    }
    if (plan.value?.organization_logo) {
      return `${config.public.apiUrl}/api/assets/${plan.value.organization_logo}?width=1200&height=630&fit=contain&format=jpg&quality=80&background=ffffff`;
    }
    return fallbackOgImage;
  });

  // Canonical = slug du plan-LEADER du groupe (H14 SEO) : les slugs des autres
  // tarifs servent la même page (mêmes tarifs, même title de groupe) → sans ça,
  // Google indexe N URLs dupliquées (3 pages MJR au title identique, audit
  // SemRush 2026-08-05). Le sitemap n'émet déjà QUE le leader ; on aligne la
  // page. Fallback slug courant (API pas encore à jour ou plan mono-tarif).
  const canonicalUrl = computed(
    () =>
      `https://donoor.org/a/${plan.value?.group_leader_slug || slug.value}`,
  );

  // Title enrichi : "Adhésion U14 — Club Tennis Paris — Paris"
  // (ajout ville pour les requêtes géo type "club tennis paris").
  // Pas de suffixe « | Donoor » : le titleTemplate global est "%s" (aucun
  // suffixe de marque — choix Karim, on cible les recherches du contenu).
  const seoTitle = computed(() => {
    if (!plan.value) return t("adhesion.useMembershipCheckoutSeo.titreParDefaut");
    const parts: string[] = [displayTitle.value];
    if (plan.value.organization_name) parts.push(plan.value.organization_name);
    // Ville du lieu (§10) : « Saint-Denis, La Réunion », pas « Saint-Denis »
    // seul (homonyme du 93) ; « Bruxelles, Belgique » hors de France.
    const ville = villeCourteLieu(plan.value);
    if (ville) parts.push(ville);
    return parts.join(" — ").substring(0, 60);
  });

  // Noindex sur statuts non-publics. Un plan en `draft` ou `archived` ne doit
  // pas apparaître dans la SERP. La page renvoie aussi un 404 HTTP côté SSR
  // dans ces cas, mais on bloque aussi côté meta pour les crawlers anciens.
  const shouldNoindex = computed(() => {
    if (!plan.value) return true;
    // Adhésion PRIVÉE (migration 285) : accessible par son lien, jamais
    // indexée — c'est sa raison d'être. Oubliée ici alors que l'événement et
    // la boutique l'appliquaient : une adhésion privée dont le lien circulait
    // pouvait finir dans Google (Karim 2026-09-23, audit).
    if ((plan.value as { is_public?: boolean | null }).is_public === false) return true;
    const blockedStatuses: MembershipPlanStatus[] = ["draft", "archived"];
    return blockedStatuses.includes(plan.value.status);
  });

  // robots cohérent avec l'indexabilité globale (staging = noindex) + noindex
  // page si le plan n'est pas dans un statut indexable.
  const robotsContent = useRobotsContent(shouldNoindex);

  // Démo intégrée à l'accueil : la page s'affiche DANS la home, elle ne doit
  // rien écrire dans son <head> (titre, robots, canonique, JSON-LD).
  const resultat = { planCoverImageId, ogDescription, ogImageUrl, canonicalUrl };
  if (useDemoAccueil().estIntegree) return resultat;

  useHead({
    title: seoTitle,
    link: [{ rel: "canonical", key: "canonical", href: canonicalUrl.value }],
    meta: computed(() => [{ name: "robots", content: robotsContent.value }]),
    script: computed(() => {
      if (!plan.value || shouldNoindex.value) return [];
      const price = Number(plan.value.price || 0);
      const orgName = plan.value.organization_name || "Association";
      const orgUrl = plan.value.organization_slug
        ? `https://donoor.org/associations/${plan.value.organization_slug}`
        : "https://donoor.org";

      const productLd: Record<string, unknown> = {
        "@context": "https://schema.org",
        "@type": "Product",
        name: displayTitle.value,
        description: ogDescription.value,
        url: canonicalUrl.value,
        ...(ogImageUrl.value !== fallbackOgImage && { image: ogImageUrl.value }),
        offers: {
          "@type": "Offer",
          price: price.toFixed(2),
          priceCurrency: "EUR",
          availability: "https://schema.org/InStock",
          url: canonicalUrl.value,
          seller: { "@type": "Organization", name: orgName, url: orgUrl },
        },
        brand: { "@type": "Organization", name: orgName, url: orgUrl },
      };

      // BreadcrumbList : Accueil → Adhésions → Cet adhésion (avec item URL
      // sur chaque entrée, sans quoi Google ignore parfois le rich snippet).
      const breadcrumbLd = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: t("adhesion.useMembershipCheckoutSeo.accueil"), item: "https://donoor.org" },
          { "@type": "ListItem", position: 2, name: t("adhesion.useMembershipCheckoutSeo.adhesionsBreadcrumb"), item: "https://donoor.org/recherche/adhesions" },
          { "@type": "ListItem", position: 3, name: displayTitle.value, item: canonicalUrl.value },
        ],
      };

      return [
        { type: "application/ld+json", innerHTML: JSON.stringify(productLd) },
        { type: "application/ld+json", innerHTML: JSON.stringify(breadcrumbLd) },
      ];
    }),
  });

  useSeoMeta({
    title: () => seoTitle.value,
    description: () => ogDescription.value,
    // filter(Boolean) : pas de « Titre —  » orphelin quand l'asso n'a pas
    // de nom (tiret + espace traînant visibles dans l'embed Discord).
    ogTitle: () =>
      displayTitle.value
        ? [displayTitle.value, plan.value?.organization_name].filter(Boolean).join(" — ")
        : t("adhesion.useMembershipCheckoutSeo.titreParDefaut"),
    ogDescription: () => ogDescription.value,
    ogImage: () => ogImageUrl.value,
    ogImageWidth: 1200,
    ogImageHeight: 630,
    ogUrl: () => canonicalUrl.value,
    ogSiteName: "Donoor",
    ogLocale: () => LOCALE_INTL[locale.value as Langue]?.replace("-", "_") ?? "fr_FR",
    twitterCard: "summary_large_image",
    twitterTitle: () => displayTitle.value || t("adhesion.useMembershipCheckoutSeo.titreParDefaut"),
    twitterDescription: () => ogDescription.value,
    twitterImage: () => ogImageUrl.value,
    robots: () => robotsContent.value,
  });

  return resultat;
}
